/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable sonarjs/cognitive-complexity*/
import { useMutation } from '@tanstack/react-query';
import Evaporate from 'evaporate';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Image, ProgressBar, Row } from 'react-bootstrap';
import { Control, Controller, FieldError, FieldValues } from 'react-hook-form';
import { FaPaperclip } from 'react-icons/fa6';
import createHash from 'sha.js';
import SparkMD5 from 'spark-md5';
import { api } from '../../../api/core/request';
import { isImageFilename, removeQueryStringFromUrl } from '../../../utils/files';
import { Urls } from '../../../utils/urls';
import { FormSubmitContext } from '../../contexts/FormSubmitContext';
import { handleApiError } from '../../utils/errorhandling';

export enum StatusEnum {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

interface _FileUploaderProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  dest: string;
  className?: string;
  containerClassName?: string;
  name: string;
  invalid: boolean;
  isTouched: boolean;
  isClearable: boolean;
  isDirty: boolean;
  isDownloadable?: boolean;
  error?: FieldError;
  value: string;
  disabledPreview?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: string | null) => void;
  onRemove?: () => void;
  onStatusChange?: (status: StatusEnum) => void;
  disabledDownload?: boolean;
  maxSize?: number;
  removeButtonText?: string;
}

const _FileUploader = ({
  dest,
  className,
  containerClassName,
  onChange,
  onBlur,
  onRemove,
  value: externalValue,
  name,
  disabled,
  invalid,
  isTouched,
  isClearable,
  isDirty,
  isDownloadable,
  readOnly,
  error: externalError,
  onStatusChange,
  disabledPreview,
  control,
  maxSize,
  accept = '*',
  disabledDownload,
  removeButtonText = 'Remove File',
  ...props
}: _FileUploaderProps) => {
  const destCheckUrl = Urls.s3direct();
  const signerUrl = Urls.s3directSigning();
  const [status, setStatus] = useState(StatusEnum.NOT_STARTED);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | undefined>();
  const [fileName, setFileName] = useState<string>();
  const [imgSrc, setImgSrc] = useState<string>();
  const [value, setValue] = useState<string>('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [concurrentUploads, setConcurrentUploads] = useState(0);
  const [cancelHandler, setCancelHandler] = useState<() => void>(() => () => {});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const controlledInputRef = useRef<HTMLInputElement>(null);
  const { setIsSubmitEnabled } = useContext(FormSubmitContext);

  const mutationGetUploadParams = useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post(destCheckUrl, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      return data;
    },
    onError: (error) => {
      setIsSubmitEnabled(true);
      setStatus(StatusEnum.FAILED);
      if (onStatusChange) onStatusChange(StatusEnum.FAILED);
      setError(error?.message);
      handleApiError(error, { logToConsole: false });
    },
  });

  const mutationSignRequest = useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post(signerUrl, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      return data;
    },
    onError: (error) => {
      setIsSubmitEnabled(true);
      setStatus(StatusEnum.FAILED);
      handleApiError(error);
    },
  });

  useEffect(() => {
    if (concurrentUploads > 0) setIsDisabled(true);
    else setIsDisabled(false);
  }, [concurrentUploads]);

  useEffect(() => {
    if (externalValue) {
      const strippedUrl = removeQueryStringFromUrl(decodeURI(externalValue));
      const filename = decodeURI(strippedUrl.split('/').pop()!);
      setFileName(filename);
      setValue(strippedUrl);
      if (
        filename &&
        isImageFilename(filename) &&
        strippedUrl !== externalValue &&
        externalValue.includes('?') &&
        !disabledPreview
      ) {
        setImgSrc(externalValue);
      }
      setIsSubmitEnabled(true);
      setStatus(StatusEnum.COMPLETED);
      if (onStatusChange) onStatusChange(StatusEnum.COMPLETED);
    }
  }, [externalValue, disabledPreview, setIsSubmitEnabled]);

  useEffect(() => {
    if (externalError) {
      setIsSubmitEnabled(true);
      setStatus(StatusEnum.FAILED);
      if (onStatusChange) onStatusChange(StatusEnum.FAILED);
      setError(externalError.message);
    }
  }, [externalError]);

  const disableSubmit = (status) => {
    if (status === true) setConcurrentUploads(concurrentUploads + 1);
    else setConcurrentUploads(concurrentUploads - 1);
  };

  const finishUpload = (endpoint, bucket, objectKey) => {
    const fileUrl = `${endpoint}/${bucket}/${objectKey}`;
    setValue(fileUrl);
    disableSubmit(false);
    setIsSubmitEnabled(true);
    setStatus(StatusEnum.COMPLETED);
    if (onStatusChange) onStatusChange(StatusEnum.COMPLETED);
    if (onChange) onChange(fileUrl);
  };

  const computeMd5 = (data) => {
    return btoa(SparkMD5.ArrayBuffer.hash(data, true));
  };

  const computeSha256 = (data) => {
    return createHash('sha256').update(data, 'utf-8').digest('hex');
  };

  const generateAmzInitHeaders = (acl, serverSideEncryption, sessionToken) => {
    const headers = {};
    if (acl) headers['x-amz-acl'] = acl;
    if (sessionToken) headers['x-amz-security-token'] = sessionToken;
    if (serverSideEncryption) {
      headers['x-amz-server-side-encryption'] = serverSideEncryption;
    }
    return headers;
  };

  const generateAmzCommonHeaders = (sessionToken) => {
    const headers = {};
    if (sessionToken) headers['x-amz-security-token'] = sessionToken;
    return headers;
  };

  const generateCustomAuthMethod = (signingUrl, dest) => {
    return async (
      _signParams,
      _signHeaders,
      stringToSign,
      signatureDateTime,
      _canonicalRequest
    ) => {
      const form = new FormData();
      form.append('to_sign', stringToSign);
      form.append('datetime', signatureDateTime);
      form.append('dest', dest);

      const data = await mutationSignRequest.mutateAsync(form);
      return data.s3ObjKey;
    };
  };

  const initiateUpload = (signingUrl, uploadParameters, file, dest) => {
    const createConfig = {
      customAuthMethod: generateCustomAuthMethod(signingUrl, dest),
      aws_key: uploadParameters.access_key_id,
      bucket: uploadParameters.bucket,
      aws_url: uploadParameters.endpoint,
      awsRegion: uploadParameters.region,
      computeContentMd5: true,
      cryptoMd5Method: computeMd5,
      cryptoHexEncodedHash256: computeSha256,
      partSize: 20 * 1024 * 1024,
      logging: true,
      allowS3ExistenceOptimization: uploadParameters.allow_existence_optimization,
      s3FileCacheHoursAgo: uploadParameters.allow_existence_optimization ? 12 : 0,
    };

    const addConfig = {
      name: uploadParameters.object_key,
      file: file,
      contentType: file.type,
      xAmzHeadersCommon: generateAmzCommonHeaders(uploadParameters.session_token),
      xAmzHeadersAtInitiate: generateAmzInitHeaders(
        uploadParameters.acl,
        uploadParameters.server_side_encryption,
        uploadParameters.session_token
      ),
      progress: (progressRatio, _stats) => {
        setProgress(progressRatio);
      },
      warn: (_warnType, _area, msg) => {
        if (msg.includes('InvalidAccessKeyId')) {
          setIsSubmitEnabled(true);
          setStatus(StatusEnum.FAILED);
          if (onStatusChange) onStatusChange(StatusEnum.FAILED);
          setError(msg);
        }
      },
    };

    const optHeaders = {};

    if (uploadParameters.cache_control) {
      optHeaders['Cache-Control'] = uploadParameters.cache_control;
    }

    if (uploadParameters.content_disposition) {
      optHeaders['Content-Disposition'] = uploadParameters.content_disposition;
    }

    addConfig['notSignedHeadersAtInitiate'] = optHeaders;

    Evaporate.create(createConfig).then((evaporate) => {
      disableSubmit(true);
      setIsSubmitEnabled(false);
      setStatus(StatusEnum.IN_PROGRESS);
      if (onStatusChange) onStatusChange(StatusEnum.IN_PROGRESS);
      setCancelHandler(() => () => {
        evaporate.cancel(`${uploadParameters.bucket}/${addConfig.name}`);
        setValue('');
        setImgSrc(undefined);
        disableSubmit(false);
        if (onChange) onChange(null);
      });

      evaporate.add(addConfig).then(
        (s3Objkey) => {
          setCancelHandler(() => {});
          finishUpload(uploadParameters.endpoint, uploadParameters.bucket, s3Objkey);
        },
        (reason) => {
          setCancelHandler(() => {});
          setIsSubmitEnabled(true);
          setStatus(StatusEnum.FAILED);
          if (onStatusChange) onStatusChange(StatusEnum.FAILED);
          setError(reason);
        }
      );
    });
  };

  const checkFileAndInitiateUpload = async (event) => {
    const file = event.target.files[0];
    const form = new FormData();

    control.setError(name, {});
    if (maxSize && file.size > maxSize * 1024 * 1024) {
      control.setError(name, {
        type: 'maxSize',
        message: `File size exceeds ${maxSize} MB`,
      });
      return;
    }

    if (file.type.startsWith('image')) {
      const reader = new FileReader();

      if (!disabledPreview) {
        reader.onloadend = function () {
          setImgSrc(reader.result?.toString());
        };
      }

      if (file) {
        reader.readAsDataURL(file);
      }
    }
    setFileName(file.name);

    form.append('dest', dest);
    form.append('name', file.name);
    form.append('type', file.type);
    form.append('size', file.size);

    const uploadParameters = await mutationGetUploadParams.mutateAsync(form);
    initiateUpload(signerUrl, uploadParameters, file, dest);
  };

  const removeUpload = () => {
    setValue('');
    if (onChange) onChange(null);
    if (onRemove) onRemove();
    setImgSrc(undefined);
    setIsSubmitEnabled(true);
    setStatus(StatusEnum.NOT_STARTED);
    if (onStatusChange) onStatusChange(StatusEnum.NOT_STARTED);
    if (fileInputRef?.current?.value) {
      fileInputRef.current.value = '';
    }
  };

  const clipFileName = (originalFilename) => {
    const splittedFilename = originalFilename.split('.');
    const extension = splittedFilename.pop();
    const filename = splittedFilename.join('.');
    return `${filename.slice(0, 50)}...${extension}`;
  };

  if (readOnly && status !== StatusEnum.COMPLETED) {
    return <div className="border rounded mb-3 px-2 py-3">Unavailable</div>;
  }

  return (
    <div
      className={`file-uploader-container ${invalid ? 'is-invalid' : ''} ${
        containerClassName ?? ''
      }`}
    >
      <div className="file-uploader">
        {(status === StatusEnum.NOT_STARTED || status === StatusEnum.FAILED) && (
          <Form.Control
            type="file"
            accept={accept}
            className={className}
            disabled={isDisabled || disabled}
            onChange={checkFileAndInitiateUpload}
            onBlur={onBlur}
            isInvalid={invalid || !!error}
            ref={fileInputRef}
          />
        )}
        {status === StatusEnum.IN_PROGRESS && (
          <div className="border rounded mb-3 p-2">
            <Row className="align-items-center">
              <Col xs={12}>
                <FaPaperclip /> {clipFileName(fileName)}
              </Col>
              <Col xs="auto" className="flex-grow-1">
                <ProgressBar
                  className="my-3"
                  variant="success"
                  animated
                  now={progress * 100}
                  label={`${Math.floor(progress * 100)}%`}
                />
              </Col>
              {isClearable && (
                <Col xs="auto">
                  <Button onClick={cancelHandler}>Cancel Upload</Button>
                </Col>
              )}
            </Row>
          </div>
        )}
        {status === StatusEnum.COMPLETED && (
          <div className="border rounded p-2">
            <Row className="d-flex align-items-center gx-3">
              {!!imgSrc && (
                <Col xs="auto">
                  <Image src={imgSrc} thumbnail width="64px" />
                </Col>
              )}
              <Col xs="auto" className="flex-grow-1 d-flex align-items-center">
                <FaPaperclip className="me-1" /> {clipFileName(fileName)}
              </Col>
              {isDownloadable && (
                <Col xs="auto">
                  <Button
                    disabled={disabledDownload}
                    href={externalValue}
                    target="_blank"
                    className="btn-sm"
                  >
                    Download
                  </Button>
                </Col>
              )}
              {isClearable && (
                <Col xs="auto">
                  <Button onClick={removeUpload} className="btn-sm">
                    {removeButtonText}
                  </Button>
                </Col>
              )}
            </Row>
          </div>
        )}
        <input
          type="text"
          ref={controlledInputRef}
          name={name}
          className="d-none"
          onChange={onChange ? () => onChange(value) : () => {}}
          value={value}
          {...props}
        />
      </div>
    </div>
  );
};

interface FileUploaderProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any>;
  isInvalid?: boolean;
  dest: string;
  className?: string;
  containerClassName?: string;
  isClearable?: boolean;
  isDownloadable?: boolean;
  disabledPreview?: boolean;
  disabledDownload?: boolean;
  onChange?: (value: string | null) => void;
  onRemove?: () => void;
  onStatusChange?: (status: StatusEnum) => void;
  maxSize?: number;
  removeButtonText?: string;
}

export const FileUploader = ({
  name,
  control,
  isInvalid,
  isClearable = true,
  isDownloadable = true,
  removeButtonText = 'Remove File',
  onChange,
  ...props
}: FileUploaderProps) => (
  <Controller
    name={name}
    control={control}
    render={({
      field: { onChange: controllerOnChange, onBlur, value, name },
      fieldState: { invalid, isTouched, isDirty, error },
    }) => (
      <_FileUploader
        onChange={(obj) => {
          if (obj === value) return;
          controllerOnChange(obj);
          if (onChange) onChange(obj);
        }}
        containerClassName="bg-white"
        onBlur={onBlur}
        value={value}
        name={name}
        control={control}
        isClearable={isClearable}
        removeButtonText={removeButtonText}
        invalid={isInvalid || invalid}
        isTouched={isTouched}
        isDirty={isDirty}
        isDownloadable={isDownloadable}
        error={error}
        {...props}
      />
    )}
  />
);
